import { action, makeObservable, observable, runInAction } from 'mobx';

import { CompanyBenefit, CompanyPartner, Parent, ParentService, User, companyPartnerIds } from 'us-web-services';
import { Storage } from '../../constants';

export class ParentStore {
  parent: Parent;
  hasCariloopPartnership: boolean = false;
  hasKinsidePartnership: boolean = false;
  hasOutschoolPartnership: boolean = false;
  hasActivityHeroPartnership: boolean = false;
  hasOonBenefit: boolean = false;
  hasByopBenefit: boolean = false;
  oonDescription: string = '';
  oonFormUrl: string = '';
  byopDescription: string = '';
  parentUpdated: boolean = false;
  isActiveEmployee: boolean = false;
  callback?: () => void;

  constructor() {
    const localParent = localStorage.getItem(Storage.US_PARENT_OBJECT);
    const localUser = localStorage.getItem(Storage.US_USER_OBJECT);

    this.parent = localParent ? JSON.parse(localParent) as Parent : {
      id: localUser ? (JSON.parse(localUser) as User).id : undefined,
      address: {
        city: '',
        neighborhood: '',
        state: '',
        zip: '',
      }
    };

    makeObservable(this, {
      parent: observable,
      hasCariloopPartnership: observable,
      hasKinsidePartnership: observable,
      hasOutschoolPartnership: observable,
      hasActivityHeroPartnership: observable,
      hasOonBenefit: observable,
      hasByopBenefit: observable,
      isActiveEmployee: observable,
      updateParent: action,
    });

    if (this.parent.id) {
      this.checkPartnerships();
      this.checkBenefits();

      if (this.parent.companyEmployee) {
        this.isActiveEmployee = true;
      }
    }
  }

  checkParentUpdated = (callback: () => void, openModal: () => void): void => {
    if (this.parentUpdated) {
      callback();
    } else {
      this.callback = callback;
      openModal();
    }
  }

  checkPartnerships(): void {
    if (!this.parent.companyEmployee || !this.parent.companyEmployee.company?.partners) {
      return;
    }

    const partners = this.parent.companyEmployee.company.partners as CompanyPartner[];

    if (partners.length > 0) {
      if (partners.find(partner => partner.partner?.id === companyPartnerIds.CARILOOP)) {
        this.hasCariloopPartnership = true;
      }

      if (partners.find(partner => partner.partner?.id === companyPartnerIds.KINSIDE)) {
        this.hasKinsidePartnership = true;
      }

      if (partners.find(partner => partner.partner?.id === companyPartnerIds.OUTSCHOOL)) {
        this.hasOutschoolPartnership = true;
      }

      if (partners.find(partner => partner.partner?.id === companyPartnerIds.ACTIVITY_HERO)) {
        this.hasActivityHeroPartnership = true;
      }
    }
  }

  checkBenefits(): void {
    if (!this.parent.companyEmployee || !this.parent.companyEmployee.company?.benefits) {
      return;
    }

    const benefits = this.parent.companyEmployee.company.benefits as CompanyBenefit[];

    if (benefits.length > 0) {
      const oonBenefit = benefits.find(benefit => benefit.hasOon)
      if (oonBenefit) {
        this.hasOonBenefit = true;
        this.oonDescription = oonBenefit.oonDescription || '';
        this.oonFormUrl = oonBenefit.oonFormUrl || '';
      }

      const byopBenefit = benefits.find(benefit => benefit.hasByop)
      if (byopBenefit) {
        this.hasByopBenefit = true;
        this.byopDescription = byopBenefit.byopDescription || '';
      }
    }
  }

  getNumberOfChildren(): number {
    return this.parent.kids ? this.parent.kids.length : 1;
  }

  hasValidAddress(): boolean {
    return !!this.parent.id
      && !!this.parent.address
      && !!this.parent.address.city
      && !!this.parent.address.state
      && !!this.parent.address.zip;
  }

  async refreshParent(): Promise<void> {
    if (this.parent && this.parent.id) {
      try {
        const response = (await ParentService.get(this.parent.id)).data;

        // needs a runInAction after an async call
        runInAction(() => {
          this.updateParent(response.data);
        });
      } catch (error) {
        console.warn('Refreshing parent failed', error);
      }
    } else {
      this.updateParent({});
    }
  }

  updateParent(parent: Parent): void {
    if (parent && parent.id) {
      this.parent = parent;
      localStorage.setItem(Storage.US_PARENT_OBJECT, JSON.stringify(this.parent));

      this.parentUpdated = true;
      if (this.callback) {
        this.callback();
        this.callback = undefined;
      }
    } else {
      this.parent = {};
      localStorage.removeItem(Storage.US_PARENT_OBJECT);
      localStorage.removeItem(Storage.US_SITTER_OBJECT);
      localStorage.removeItem(Storage.US_USER_OBJECT);
    }
  }
}
